import { useLayoutEffect, useRef } from 'react'

import { type useLoadMore } from '@/lib/use-load-more'
import { Button } from '@/ui/button'
import { RetryNotice } from '@/ui/retry-notice'
import { cn } from '@/lib/utils'

type Pager = ReturnType<typeof useLoadMore<unknown>>

export function LoadMoreButton({
  pager,
  size = 'sm',
  visibleStatus = false,
  retryOnFailure = false,
  className,
}: {
  pager: Pager
  size?: 'sm' | 'xs'
  visibleStatus?: boolean
  retryOnFailure?: boolean
  className?: string
}) {
  const statusVisible = visibleStatus && !pager.hasNextPage && pager.items.length > 0
  const showsRetry = retryOnFailure && pager.isFetchNextPageError
  const buttonRendered = !showsRetry && pager.hasNextPage
  const controlHadFocus = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const retryRef = useRef<HTMLButtonElement>(null)
  // The refs lint rejects pager.statusProps.ref in a dependency array, so the
  // hand-off reads the latest pager through a ref updated in the same commit.
  const latestPager = useRef(pager)
  useLayoutEffect(() => {
    latestPager.current = pager
  })
  useLayoutEffect(() => {
    const hadFocus = controlHadFocus.current
    controlHadFocus.current = false
    if (!hadFocus || document.activeElement !== document.body) return
    const target = showsRetry
      ? retryRef.current
      : buttonRendered
        ? buttonRef.current
        : latestPager.current.statusProps.ref.current
    target?.focus({ preventScroll: true })
  }, [buttonRendered, showsRetry])

  return (
    <>
      <p
        data-slot="load-more-status"
        {...pager.statusProps}
        className={cn(
          statusVisible ? 'text-muted-foreground p-3 text-xs' : pager.statusProps.className,
        )}
      />
      <span
        className="contents"
        onFocus={() => {
          controlHadFocus.current = true
        }}
        onBlur={(event) => {
          if (event.relatedTarget || event.target.isConnected) controlHadFocus.current = false
        }}
      >
        {showsRetry ? (
          <RetryNotice
            className={className}
            message={pager.retryMessage}
            onRetry={pager.buttonProps.onClick}
            disabled={pager.buttonProps.disabled}
            buttonRef={retryRef}
          />
        ) : pager.hasNextPage ? (
          <Button
            ref={buttonRef}
            data-slot="load-more-button"
            {...pager.buttonProps}
            focusableWhenDisabled
            variant="outline"
            size={size}
            className={className}
          >
            {pager.label}
          </Button>
        ) : null}
      </span>
    </>
  )
}
